import type { StudentReviewDataLoadedStatus } from "p6m-contexts";
import { StudentResultLoadFailures, StudentReviewResultLoadStatuses } from "../enums/studentReview";

export enum SharedResultAccessStates {
    PENDING = "PENDING",
    ALLOW = "ALLOW",
    LOGIN_REQUIRED = "LOGIN_REQUIRED",
    WRONG_MEMBER = "WRONG_MEMBER",
}

export interface SharedResultAccessParams {
    dataLoaded: StudentReviewDataLoadedStatus;
    isRestoringSession: boolean;
    resultLoadStatus: StudentReviewResultLoadStatuses;
    resultClassMemberId: string | undefined;
    memberId: string | undefined;
}

/**
 * A result carrying a classMemberId may only be viewed by that member; legacy anonymous share links
 * carry none and stay open. A failed fetch is ALLOW so the view can show its own message. The load
 * checks come first so open links never wait on session restore.
 */
export const resolveSharedResultAccess = (params: SharedResultAccessParams): SharedResultAccessStates => {
    // Unknown shareId: the fetch never starts, so resultLoadStatus settles to ERROR alongside NOT_FOUND.
    if (params.dataLoaded === "NOT_FOUND") return SharedResultAccessStates.ALLOW;
    if (params.resultLoadStatus === StudentReviewResultLoadStatuses.PENDING) return SharedResultAccessStates.PENDING;
    if (params.resultLoadStatus === StudentReviewResultLoadStatuses.ERROR) return SharedResultAccessStates.ALLOW;
    if (!params.resultClassMemberId) return SharedResultAccessStates.ALLOW;
    if (params.isRestoringSession) return SharedResultAccessStates.PENDING;
    if (!params.memberId) return SharedResultAccessStates.LOGIN_REQUIRED;
    return params.memberId === params.resultClassMemberId
        ? SharedResultAccessStates.ALLOW
        : SharedResultAccessStates.WRONG_MEMBER;
};

/**
 * Whether retrying could help. "NOT_FOUND" is set only where the server answered and had no feedback
 * record; a request that merely failed leaves dataLoaded alone, so it lands here as retryable.
 */
export const getStudentResultLoadFailure = (dataLoaded: StudentReviewDataLoadedStatus): StudentResultLoadFailures => {
    return dataLoaded === "NOT_FOUND" ? StudentResultLoadFailures.NOT_FOUND : StudentResultLoadFailures.NOT_LOADED;
};
